import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { HaleWordmark } from '../../components/HaleBrand';
import { CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { HALE_BG, HALE_TEXT_FAINT } from './_design';
import TI from '../../components/brand/TI';

/* /hale/pathway/:id — post-submit confirmation screen.
 *
 * No pricing, tiers, fees, or quotes are shown anywhere in the public
 * application flow (commercial model is intentionally deferred until
 * after portfolio/SKU/retailer scope is understood by the Hale team).
 * This screen simply confirms receipt and sets expectations for the
 * Apply → Review → Conversation → Proposal journey.
 */

const WHAT_HAPPENS_NEXT = [
  'Application submitted.',
  'Hale review team evaluates your submission.',
  'Scientific review requirements are determined.',
  'Marketplace and partner opportunities are discussed.',
  'A customized scope and proposal are provided if accepted.',
];

export default function HalePathwayPage() {
  const { id } = useParams();
  const appRef = id ? `HALE-${String(id).slice(-8).toUpperCase()}` : null;

  return (
    <div className="min-h-screen text-[#0B1426]" style={{ backgroundColor: HALE_BG }}>
      <header className="border-b px-6 lg:px-12 py-5" style={{ borderColor: 'rgba(11,20,38,0.06)' }}>
        <div className="max-w-6xl mx-auto"><HaleWordmark size="sm" /></div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-0 py-20 lg:py-28">
        <div className="flex items-center gap-2 mb-8" data-testid="pathway-badge">
          <CheckCircle size={16} weight="fill" style={{ color: '#E63946' }} />
          <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E63946]">
            Application received
          </p>
        </div>

        <h1
          className="text-4xl lg:text-6xl leading-[1.05] tracking-tight font-light"
          style={{ fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
          data-testid="pathway-title"
        >
          Thank you for your application.
        </h1>

        <p className="mt-8 text-lg leading-[1.65] max-w-2xl" style={{ color: 'rgba(11,20,38,0.8)' }} data-testid="pathway-subtext">
          Based on your responses, your application has been routed to the appropriate Hale review
          pathway. A member of the Hale team will review your submission and contact you regarding
          next steps.
        </p>

        {appRef && (
          <div className="mt-8 inline-flex items-center gap-3 rounded-md px-4 py-2.5"
               style={{ backgroundColor: 'rgba(11,20,38,0.04)', border: '1px solid rgba(11,20,38,0.08)' }}
               data-testid="pathway-application-id">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#0B1426]/40">Application ID</span>
            <span className="text-[13px] font-mono tracking-wider text-[#0B1426]">{appRef}</span>
          </div>
        )}

        {/* What happens next */}
        <div className="mt-16 rounded-lg p-7"
             style={{ backgroundColor: 'rgba(11,20,38,0.03)', border: '1px solid rgba(11,20,38,0.06)' }}>
          <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E63946] mb-5">What happens next</p>
          <ol className="space-y-3 text-[15px] text-[#0B1426]/85">
            {WHAT_HAPPENS_NEXT.map((s, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`pathway-next-${i}`}>
                <span className="font-mono text-[#0B1426]/40 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>

        <p className="mt-8 text-[13px] max-w-xl" style={{ color: HALE_TEXT_FAINT }}>
          A confirmation has been sent to the email on your application. You can close this window —
          the Hale team will be in touch.
        </p>

        {/* CTA */}
        <div className="mt-12 flex gap-3 flex-wrap">
          <Link to="/" data-testid="pathway-home">
            <Button className="h-11 px-7 text-[11px] font-bold uppercase tracking-[0.22em] rounded-full bg-[#E63946] hover:bg-[#C9192C] text-white">
              Return to Hale homepage <ArrowRight size={14} className="ml-2" weight="bold" />
            </Button>
          </Link>
          <Link to="/hale/dashboard" data-testid="pathway-dashboard">
            <Button variant="outline" className="h-11 px-7 text-[11px] font-bold uppercase tracking-[0.22em] rounded-full bg-transparent border-[#0B1426]/20 text-[#0B1426]/80 hover:bg-[#0B1426]/5">
              Go to your dashboard
            </Button>
          </Link>
        </div>
      </main>

      <footer className="border-t px-6 py-8 mt-16" style={{ borderColor: 'rgba(11,20,38,0.06)' }}>
        <p
          className="text-center text-[10px] mt-1 max-w-3xl mx-auto leading-relaxed"
          style={{ color: HALE_TEXT_FAINT }}
          data-testid="hale-platform-footnote"
        >
          HALE onboarding is powered by <TI color="#E63946" />. Scientific review outputs are
          routed through the <TI color="#E63946" /> review infrastructure.
        </p>
      </footer>
    </div>
  );
}
